/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef } from "react";
import { Radio, FormControl, Input, InputAdornment, DialogContent, DialogTitle, DialogActions } from "@mui/material";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import cn from "clsx";
import styles from "./index.module.scss";
// 组件
import EmptyPage from "../emptyPage";
import GoogleMaps from "../googleLocation";
import CommonButton from "../commonButton";
import UKDialog from "@/common-components-src/components/UKDialog/index";
import UKLoadingIcon from "@/common-components-src/components/UKLoadingIcon";

// 接口
import to from "await-to-js";
import { fetchPostHouseList } from "@/app/csr-src/apis/afterSales/afterSales.api";
import { PostHouseListResVo } from "@/app/csr-src/apis/afterSales/afterSales.interface";
// 图标
import Closed from "@/app/csr-src/assets/icons/Closed.svg";
import SearchIcon from "@/app/csr-src/assets/icons/search.svg";
import VectorDown from "@/app/csr-src/assets/icons/VectorDown.svg";

interface locationModalProps {
  isOpen: boolean;
  selectValue: string; // 驿站的houseId
  selectPostCode: string; // 驿站的postCode
  // selectList: PostHouseListResVo[];
  selectChangeHandler: (value: any) => void;
  initPostCode: string;
  closeHandler: () => void;
}

const locationModal: React.FC<locationModalProps> = observer(({ selectValue, selectPostCode, selectChangeHandler, initPostCode, closeHandler, isOpen }) => {
  const { t } = useTranslation();

  const [postCode, setPostCode] = useState("");
  const [locationList, setLocationList] = useState<PostHouseListResVo[] | []>([]);
  const [curLocation, setCurLocation] = useState<PostHouseListResVo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [curHouseId, setCurHouseId] = useState(selectValue);
  // 当前展开的项ID（null 表示全部收起）
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const searchBar = useRef<HTMLDivElement>(null);
  const searchList = useRef<HTMLDivElement>(null);

  // if (!selectList?.length) {
  //   return null;
  // }

  useEffect(() => {
    loadPostHouseList(selectPostCode || initPostCode, true);
  }, [selectPostCode]);

  // 防抖函数
  const debounce = (func: () => void, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(func, delay);
    };
  };

  // 获取驿站信息列表
  const loadPostHouseList = async (val?: string, isFirst?: boolean) => {
    setLoading(true);
    const postCodeParams = val ? val : postCode ? postCode : initPostCode;
    const [err, postHouseListData] = await to(
      fetchPostHouseList({
        postCode: postCodeParams,
      }),
    );

    if (err) {
      // setError("Failed to fetch address list");
      setLocationList([]);
    } else {
      if (isFirst && selectPostCode) {
        const selectHouseItem = postHouseListData.find((item) => item.postCode === selectPostCode);
        setCurLocation(selectHouseItem as PostHouseListResVo);
      }
      setLocationList(postHouseListData as PostHouseListResVo[]);

      // setError(null);
    }
    setLoading(false);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPostCode(e.target.value);
  };

  const debouncedHandleEnter = debounce(loadPostHouseList, 300);

  // 搜索输入框回车事件
  const codeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == "Enter") {
      debouncedHandleEnter();
    }
  };

  // 选择地址
  const selectItemHandler = (item: PostHouseListResVo) => {
    setCurLocation(item);
    setCurHouseId(item.postHouseId);
  };

  const handleItemClick = (itemId: string) => {
    // 如果点击的是已展开的项，则收起；否则展开该新项
    setExpandedId(expandedId === itemId ? null : itemId);
  };

  return (
    <UKDialog
      open={isOpen}
      onClose={() => {
        closeHandler();
      }}
      showCloseBtn={true}
      sx={{
        "& .MuiDialog-paper": {
          width: "880px", // 设置自定义宽度
          height: "82vh",
        },
      }}
    >
      <DialogTitle>{t("order.orderDetail.returnRequestModal.dropOffText")}</DialogTitle>
      <DialogContent
        sx={{
          paddingBottom: "16px",
        }}
      >
        <div className={styles.selectWrap}>
          <div className={styles.searchBarWrap} ref={searchBar}>
            <FormControl fullWidth={true}>
              <Input
                autoFocus
                sx={{
                  borderRadius: "4px",
                  height: "36px",
                  fontSize: "14px",
                  border: "1px solid #1A1A1A",
                  padding: "0 12px",
                }}
                value={postCode || ""}
                type="text"
                onChange={handleCodeChange}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => codeKeyDown(e)}
                disableUnderline
                endAdornment={
                  <InputAdornment position="end">
                    {postCode ? (
                      <>
                        <div
                          onClick={() => {
                            setPostCode("");
                          }}
                          className={styles.inputEndAdornmentStyle}
                        >
                          <Closed />
                        </div>
                      </>
                    ) : null}
                    <SearchIcon />
                  </InputAdornment>
                }
                placeholder="Search drop-off location by postcode"
              />
            </FormControl>
          </div>
          <div className={styles.searchListWrap} ref={searchList}>
            {loading ? (
              <div className={styles.loadingWrap}>
                <UKLoadingIcon type="black" />
                <span className={styles.loadingText}>{t("common.pageStatus.loading")}</span>
              </div>
            ) : locationList?.length ? (
              <>
                <GoogleMaps
                  updateLocation={(location) => {
                    setCurLocation(location);
                    setCurHouseId(location.postHouseId);
                  }}
                  locationList={locationList}
                  curLocationInfo={curLocation}
                />
                <div className={styles.locationListWrap}>
                  {locationList?.map((item) => {
                    return (
                      <div
                        key={item.postHouseId}
                        className={styles.searchResultItem}
                        onClick={() => {
                          selectItemHandler(item);
                          handleItemClick(item.postCode);
                        }}
                      >
                        <div className={styles.locationItemWrap}>
                          <Radio
                            checked={curHouseId === item.postHouseId}
                            size="small"
                            sx={{
                              padding: 0,
                            }}
                          />
                          <div className={styles.locationMain}>
                            <div className={styles.locationName}>
                              {item?.postHouseName}
                              {/* {item?.distance ? <span className={styles.locationDis}>{item?.distance}</span> : null} */}
                            </div>
                            <div className={styles.locationDesc}>{item?.address}</div>
                            {expandedId === item.postCode && item?.businessHours?.length ? (
                              <>
                                <div className={styles.businessHoursTitle}>Opening time</div>
                                {item.businessHours.map((ele, index) => {
                                  return (
                                    <div key={`businessHours-${index}`} className={styles.businessHoursItem}>
                                      <div>{ele.title}</div>
                                      <div>{ele.subTitle}</div>
                                    </div>
                                  );
                                })}
                              </>
                            ) : null}
                          </div>
                        </div>
                        <VectorDown
                          className={cn(styles.moreIco, {
                            [styles.retractIco]: expandedId === item.postCode,
                          })}
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className={styles.emptyWrap}>
                <EmptyPage emptyText={"No related result, please try a different postcode."} />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions
        sx={{
          paddingBottom: "16px",
        }}
      >
        <CommonButton variant="outlined" onClick={closeHandler}>
          {t("order.orderList.common.cancelBtnText")}
        </CommonButton>
        <CommonButton
          variant="contained"
          onClick={() => {
            selectChangeHandler && selectChangeHandler(curLocation);
          }}
          disabled={!curLocation || loading}
        >
          Save
        </CommonButton>
      </DialogActions>
    </UKDialog>
  );
});

export default locationModal;
